import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetExpenseBreakdownUseCase } from "../../domain/use-cases/GetExpenseBreakdownUseCase";
import type {
  ExpenseBreakdownEntity,
  ExpenseBreakdownTimeframe,
  ExpenseBreakdownType,
} from "../../domain/entities/ExpenseBreakdownEntity";
import {
  getExpenseBootstrapData,
  getExpenseBootstrapPromise,
} from "../state/expenseBootstrapStore";
import { useExpenseBootstrap } from "./useExpenseBootstrap";

const repository = new ClientOverviewRepositoryImpl();
const getExpenseBreakdownUseCase = new GetExpenseBreakdownUseCase(repository);

export function useExpenseBreakdown(
  initialType: ExpenseBreakdownType = "vendor",
  initialTimeframe: ExpenseBreakdownTimeframe = "ytd",
) {
  useExpenseBootstrap();

  const [type, setType] = useState<ExpenseBreakdownType>(initialType);
  const [timeframe, setTimeframe] = useState<ExpenseBreakdownTimeframe>(initialTimeframe);
  const [entity, setEntity] = useState<ExpenseBreakdownEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tryHydrateVendorFromBootstrap = useCallback(async (): Promise<boolean> => {
    let bootstrapEntity = getExpenseBootstrapData();
    if (!bootstrapEntity) {
      const bootstrapPromise = getExpenseBootstrapPromise();
      if (bootstrapPromise) {
        try {
          bootstrapEntity = await bootstrapPromise;
        } catch {
          return false;
        }
      }
    }
    if (!bootstrapEntity) {
      return false;
    }
    const section = bootstrapEntity.sections.breakdown;
    if (section.status !== "ok" || !section.data) {
      return false;
    }
    setEntity(section.data);
    setError(null);
    return true;
  }, []);

  const fetchBreakdown = useCallback(
    async (
      breakdownType: ExpenseBreakdownType,
      breakdownTimeframe: ExpenseBreakdownTimeframe,
    ) => {
      try {
        setLoading(true);
        setError(null);
        const data = await getExpenseBreakdownUseCase.execute(
          breakdownType,
          breakdownTimeframe,
        );
        setEntity(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load expense breakdown";
        setError(message);
        setEntity(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void (async () => {
      if (type === "vendor" && timeframe === "ytd") {
        setLoading(true);
        setError(null);
        const ok = await tryHydrateVendorFromBootstrap();
        if (ok) {
          setLoading(false);
          return;
        }
        await fetchBreakdown("vendor", "ytd");
      } else {
        await fetchBreakdown(type, timeframe);
      }
    })();
  }, [type, timeframe, fetchBreakdown, tryHydrateVendorFromBootstrap]);

  const setBreakdownType = useCallback((newType: ExpenseBreakdownType) => {
    setType(newType);
  }, []);
  const setBreakdownTimeframe = useCallback(
    (newTimeframe: ExpenseBreakdownTimeframe) => {
      setTimeframe(newTimeframe);
    },
    [],
  );

  return {
    breakdownType: type,
    breakdownTimeframe: timeframe,
    setBreakdownType,
    setBreakdownTimeframe,
    items: entity?.items ?? [],
    totalExpense: entity?.totalExpense ?? 0,
    summary: entity?.summary,
    periodLabel: entity?.periodLabel ?? "FY Expenses (Closed)",
    dateRangeStart: entity?.dateRangeStart,
    dateRangeEnd: entity?.dateRangeEnd,
    breakdownUnavailableReason: entity?.breakdownUnavailableReason,
    loading,
    error,
    refetch: () => fetchBreakdown(type, timeframe),
  };
}
